import { Card } from "antd";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getHomeSliderImages } from "../../../api/Slide";

const CACHE_KEY_SLIDER = 'app_home_slider_cache';

function getCachedSlider(): any[] | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY_SLIDER);
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

function setCachedSlider(items: any[]): void {
    try {
        localStorage.setItem(CACHE_KEY_SLIDER, JSON.stringify(items));
    } catch { }
}

export default function HomeSlider() {
    const { i18n } = useTranslation();
    const [sliderImage, setSliderImage] = useState<any[]>(() => getCachedSlider() || []);
    const [loading, setLoading] = useState(() => !getCachedSlider());
    const [swiperRef, setSwiperRef] = useState<any>(null);

    useEffect(() => {
        const fetchSliderData = async () => {
            if (!sliderImage.length) setLoading(true);
            try {
                const data = await getHomeSliderImages(i18n.language);

                console.log("Fetched slider data:", data);
                if (data.status) {
                    setSliderImage(data.data.items);
                    setCachedSlider(data.data.items);
                } else {
                    console.warn("API returned unsuccessful status:", data.msg);
                }
            } catch (error) {
                console.error("Failed to fetch slider data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSliderData();
    }, [i18n.language]);

    function getYoutubeEmbedUrl(url?: string) {
        if (!url) return null;

        try {
            const parsed = new URL(url);

            if (
                parsed.hostname.includes("youtube.com") ||
                parsed.hostname.includes("youtu.be")
            ) {
                let videoId = "";

                if (parsed.hostname.includes("youtu.be")) {
                    videoId = parsed.pathname.slice(1);
                } else {
                    videoId = parsed.searchParams.get("v") || "";
                }

                return videoId
                    ? `https://www.youtube.com/embed/${videoId}`
                    : null;
            }
        } catch (e) { }

        return null;
    }

    function handleSlideChange(swiper: any) {
        const slides = swiper.slides;

        slides.forEach((slide: any) => {
            const iframe = slide.querySelector("iframe");

            if (iframe) {
                // pause tất cả video
                iframe.contentWindow?.postMessage(
                    '{"event":"command","func":"pauseVideo","args":""}',
                    "*"
                );
            }
        });

        const activeSlide = slides[swiper.activeIndex];
        const activeIframe = activeSlide?.querySelector("iframe");

        if (activeIframe) {
            // play video hiện tại
            activeIframe.contentWindow?.postMessage(
                '{"event":"command","func":"playVideo","args":""}',
                "*"
            );

            swiper.autoplay.stop(); // dừng autoplay khi xem video
        } else {
            swiper.autoplay.start(); // tiếp tục autoplay nếu là ảnh
        }
    }

    return (
        <div
            className="py-6"
            style={{ backgroundColor: "rgba(21, 169, 89, 0.1)" }}
        >
            <div className="styles_backgroundImage__ZSdC3"></div>
            <div className="max-w-[1200px] mx-auto relative z-1">
                <Card
                    bodyStyle={{ padding: 0 }}
                    className="!p-[10px] max-h-[490px] rounded-[12px] bg-white"
                >
                    <div className="relative">
                        <Swiper
                            modules={[Navigation, Pagination, Autoplay]}
                            navigation={{
                                prevEl: ".prev-btn",
                                nextEl: ".next-btn",
                            }}
                            pagination={{ clickable: true }}
                            // autoplay={{ delay: 10000 }}
                            loop
                            className="home-slider"
                            onSwiper={setSwiperRef}
                            onSlideChange={(swiper) => handleSlideChange(swiper)}
                            breakpoints={{
                                0: {
                                    pagination: false, // mobile vẫn có dots
                                },
                                1199: {
                                    pagination: { clickable: true },
                                },
                            }}
                        >
                            {loading ? (
                                <SwiperSlide>
                                    <div className="w-full h-[450px] bg-gray-200 animate-pulse rounded-md" />
                                </SwiperSlide>
                            ) : sliderImage.length > 0 ? (
                                sliderImage.map((item) => {
                                    const youtubeEmbed = getYoutubeEmbedUrl(item.link_video || item.link);
                                    const videoId = youtubeEmbed?.split("/embed/")[1];

                                    return (
                                        <SwiperSlide key={item.id}>
                                            {youtubeEmbed ? (
                                                <a href={item.link} className="block" target="_blank" rel="noreferrer">
                                                    <div className="relative w-full md:!h-[450px] h-[full]">
                                                        <iframe
                                                            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1&rel=0`}
                                                            className="w-full h-full"
                                                            allow="autoplay; encrypted-media"
                                                            allowFullScreen
                                                        />

                                                        {/* overlay chặn hover */}
                                                        <div className="absolute inset-0 z-10"></div>
                                                    </div>
                                                </a>
                                            ) : item.link?.startsWith("http") ? (
                                                <a href={item.link} className="block" target="_blank" rel="noreferrer">
                                                    <img
                                                        src={item.avatar}
                                                        className="w-full !h-full object-cover md:!h-[450px]"
                                                        alt={item.name}
                                                    />
                                                </a>
                                            ) : (
                                                <Link to={item.link || "/"} className="block">
                                                    <img
                                                        src={item.avatar}
                                                        className="w-full !h-full object-cover md:!h-[450px]"
                                                        alt={item.name}
                                                    />
                                                </Link>
                                            )}
                                        </SwiperSlide>
                                    );
                                })
                            ) : (
                                <SwiperSlide>
                                    <div className="w-full h-[450px] bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-500">No slides available</span>
                                    </div>
                                </SwiperSlide>
                            )}
                        </Swiper>

                        {/* arrow */}
                        <div
                            className="hidden-1199 prev-btn absolute left-[-24px] top-1/2 -translate-y-1/2 z-10
                            bg-white w-9 h-9 rounded-full flex items-center justify-center shadow
                            cursor-pointer group transition-all duration-200 hover:!bg-[var(--primary-color)]"
                        >
                            <ChevronLeft
                                size={32}
                                strokeWidth={3}
                                style={{ color: "var(--primary-color)" }}
                                className="group-hover:!text-white"
                            />
                        </div>

                        <div
                            className="next-btn absolute right-[-24px] top-1/2 -translate-y-1/2 z-10
                            bg-white w-9 h-9 rounded-full hidden-1199 flex items-center justify-center shadow
                            cursor-pointer group transition-all duration-200 hover:!bg-[var(--primary-color)]"
                        >
                            <ChevronRight
                                size={32}
                                strokeWidth={3}
                                style={{ color: "var(--primary-color)" }}
                                className="group-hover:!text-white"
                            />
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}